import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Spin, Button } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import MessageContent from './MessageContent';
import { ChatWindowProps, UIMessage } from '../../../types/chat';
import { saveMessageState } from '../../../services/chatService';
import { v4 as uuidv4 } from 'uuid';

import {
    StyledChatWindow,
    MessageContainer,
    Avatar,
    Nickname,
    DateLabel,
    MessageContentContainer
} from '../../../styles/chat';
import SuggestedQuestions from './SuggestedQuestions';
import { useMessageGrouping } from '../hooks/useMessageGrouping';

const defaultAvatar = 'https://acupoint-resource.oss-cn-shenzhen.aliyuncs.com/download/default_user_avatar.png';

// 标题栏容器
const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: sticky;
    top: 0;
    z-index: 10;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 18px;
    color: #1f1f1f;
`;

const ChatWindowContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;
`;

const ScrollableContent = styled(StyledChatWindow)`
    flex: 1;
    overflow-y: auto;
`;

const ChatWindow: React.FC<ChatWindowProps> = ({
    messages,
    onSendMessage,
    hasMore,
    onLoadMore,
    suggestedQuestions,
    lastMessageId,
    hideUIMessageSuggestions,
    isLoadingSuggestions,
    isSSE
}) => {
    const chatWindowRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const lastScrollTop = useRef(0);
    const isLoadingMore = useRef(false);
    const lastMessageRef = useRef<string | null>(null);
    const navigate = useNavigate();
    const { account_id } = useParams<{ account_id: string }>();

    const handleNewChat = () => {
        const newThreadId = uuidv4();
        navigate(`/chat/${account_id}/${newThreadId}`);
    };

    const { groupedMessages } = useMessageGrouping(messages);

    // 处理滚动事件
    const handleScroll = useCallback(() => {
        if (!chatWindowRef.current || !hasMore || isLoading || isLoadingMore.current) return;

        const { scrollTop } = chatWindowRef.current;
        const isScrollingUp = scrollTop < lastScrollTop.current;

        if (isScrollingUp && scrollTop < 50 && !isLoadingMore.current) {
            setIsLoading(true);
            isLoadingMore.current = true;

            onLoadMore?.().then(() => {
                setTimeout(() => {
                    if (chatWindowRef.current) {
                        chatWindowRef.current.scrollTop = 50;
                        setIsLoading(false);
                        isLoadingMore.current = false;
                    }
                }, 500);
            });
        }
        lastScrollTop.current = scrollTop;
    }, [hasMore, isLoading, onLoadMore]);

    // 添加滚动监听
    useEffect(() => {
        const chatWindow = chatWindowRef.current;
        if (chatWindow) {
            chatWindow.addEventListener('scroll', handleScroll);
            return () => chatWindow.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // 处理UI状态变更
    const handleUIStateChange = useCallback(async (messageId: string, state: any) => {
        try {
            await saveMessageState(messageId, state);
        } catch (error) {
            console.error('保存UI状态失败:', error);
        }
    }, []);

    // 处理消息滚动
    useEffect(() => {
        if (!chatWindowRef.current || !messages.length) return;

        const lastMessage = messages[messages.length - 1];
        if (!('id' in lastMessage) || lastMessage.id === lastMessageRef.current) return;

        lastMessageRef.current = lastMessage.id!;

        if (isSSE && lastMessage.type === 'ui') {
            setTimeout(() => {
                const messageElement = document.querySelector(`[data-message-id="${lastMessage.id}"]`);
                if (messageElement) {
                    messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);
        } else if (!isSSE && lastMessage.type === 'ui') {
            setTimeout(() => {
                chatWindowRef.current?.scrollTo({
                    top: chatWindowRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }, 500);
        } else if (!isLoadingMore.current) {
            chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }
    }, [messages, isSSE]);

    return (
        <ChatWindowContainer>
            <HeaderContainer>
                <Title>智能问诊助手</Title>
                <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={handleNewChat}
                >
                    新会话
                </Button>
            </HeaderContainer>
            <ScrollableContent ref={chatWindowRef} id="chat-window-container">
                <LoadingContainer isVisible={isLoading}>
                    <Spin size="small" />
                </LoadingContainer>
                {groupedMessages.map((msg) => (
                    msg.type === 'date' ? (
                        <DateLabel key={`date-${msg.date}`}>{msg.date}</DateLabel>
                    ) : (
                        <MessageContainer
                            key={msg.id || `msg-${msg.timestamp}`}
                            role={msg.role}
                            data-message-id={msg.id}
                        >
                            <Avatar src={msg.avatar || defaultAvatar} alt={`${msg.nickname} avatar`} />
                            <MessageContentContainer role={msg.role}>
                                <Nickname>{msg.nickname}</Nickname>
                                <MessageContent
                                    message={msg}
                                    onSendMessage={onSendMessage}
                                    onUIStateChange={msg.id ?
                                        (state) => handleUIStateChange(msg.id!, state) :
                                        undefined
                                    }
                                    initialUIState={msg.type === 'ui' ? (msg as UIMessage).ui_state : undefined}
                                />
                                {!hideUIMessageSuggestions &&
                                    msg.type !== 'ui' &&
                                    msg.id === lastMessageId &&
                                    (isLoadingSuggestions || suggestedQuestions?.questions) && (
                                        <SuggestedQuestions
                                            questions={suggestedQuestions?.questions || []}
                                            onQuestionClick={onSendMessage}
                                            isLoading={isLoadingSuggestions}
                                        />
                                    )}
                            </MessageContentContainer>
                        </MessageContainer>
                    )
                ))}
            </ScrollableContent>
        </ChatWindowContainer>
    );
};

// 添加样式
const LoadingContainer = styled.div<{ isVisible: boolean }>`
    display: ${props => props.isVisible ? 'flex' : 'none'};
    justify-content: center;
    padding: 10px;
    background: transparent;
`;

export default ChatWindow; 